import * as THREE from "three";

/**
 * Unit health bars (art review units F3), all of one team in ONE draw.
 *
 * The old bar was a world-X box per unit (0.8 wide, 3.5× the unit, scaled about its centre so it shrank from both
 * ends, green on the green meadow, depth-tested so trees hid it, one draw each). Here every bar is an instance of one
 * quad, turned to face the camera in the vertex shader: a 0.30 × 0.045 dark backing with a 1 px frame, a fill anchored
 * at the left edge in the team colour, and a white "chaser" segment that holds the lost health and runs down after it
 * over 0.35 s, so a big hit reads as a big hit. Drawn without depth test, after the scene's transparents.
 *
 * Per-bar state lives in `BarState` (one per view); each frame the view calls `begin`, `push` per visible bar, `end`.
 */
export const HP_BAR = {
  width: 0.30, height: 0.045,
  enemy: 0xe8453c, troop: 0x4fb8ff, backing: 0x1a1410,
  /** The chaser closes the gap it was given in this long. */
  chaseSeconds: 0.35,
  /** Fade in on the first damage, fade out on death. */
  fadeIn: 0.12, fadeOut: 0.18,
} as const;

/** One unit's bar: the displayed fill, the white chaser behind it and the fade. */
export class BarState {
  hp = 1;
  chase = 1;
  alpha = 0;
  private rate = 0;
  /** Hidden until the unit is first hurt (full-health units carry no bar). */
  shown = false;
  dying = false;

  reset(): void { this.hp = this.chase = 1; this.alpha = 0; this.rate = 0; this.shown = false; this.dying = false; }

  /** New health fraction from the sim (per tick). A drop starts the chaser; healing moves both up at once. */
  set(hp: number): void {
    const v = Math.max(0, Math.min(1, hp));
    if (v < this.hp) {
      if (v < 0.999) this.shown = true;
      this.rate = Math.max(this.rate, (this.chase - v) / HP_BAR.chaseSeconds);
    } else if (v > this.chase) this.chase = v;
    this.hp = v;
  }

  /** Per frame: the chaser runs down, the bar fades in (hurt) or out (dead). */
  update(dt: number): void {
    if (this.chase > this.hp) {
      this.chase = Math.max(this.hp, this.chase - this.rate * dt);
      if (this.chase === this.hp) this.rate = 0;
    }
    const want = this.shown && !this.dying ? 1 : 0;
    const step = dt / (want > this.alpha ? HP_BAR.fadeIn : HP_BAR.fadeOut);
    this.alpha = want > this.alpha ? Math.min(want, this.alpha + step) : Math.max(want, this.alpha - step);
  }
}

const VERT = /* glsl */ `
attribute vec3 aPos;
attribute vec4 aBar;
attribute vec3 aCol;
uniform vec2 uSize;
varying vec2 vUv;
varying vec4 vBar;
varying vec3 vCol;
void main() {
  vUv = uv; vBar = aBar; vCol = aCol;
  vec4 mv = modelViewMatrix * vec4(aPos, 1.0);
  mv.xy += position.xy * uSize;
  gl_Position = projectionMatrix * mv;
}`;

const FRAG = /* glsl */ `
uniform vec3 uBack;
varying vec2 vUv;
varying vec4 vBar;
varying vec3 vCol;
void main() {
  vec2 px = fwidth(vUv);
  float frame = max(max(step(vUv.x, px.x), step(1.0 - px.x, vUv.x)), max(step(vUv.y, px.y), step(1.0 - px.y, vUv.y)));
  vec2 inner = (vUv - px * 1.5) / max(vec2(1e-3), 1.0 - px * 3.0);
  vec3 col = uBack;
  if (inner.x <= vBar.x) col = vCol * (0.82 + 0.36 * clamp(inner.y, 0.0, 1.0));
  else if (inner.x <= vBar.y) col = vec3(1.0);
  col = mix(col, vec3(0.0), frame);
  gl_FragColor = linearToOutputTexel(vec4(col, vBar.z * mix(0.88, 1.0, frame)));
}`;

/** Uploads only the first `n` instances of a dynamic attribute. */
export function dirty(a: THREE.InstancedBufferAttribute, n: number): void {
  a.clearUpdateRanges();
  a.addUpdateRange(0, n * a.itemSize);
  a.needsUpdate = true;
}

export class HpBars {
  readonly mesh: THREE.Mesh;
  private readonly geo: THREE.InstancedBufferGeometry;
  private pos!: THREE.InstancedBufferAttribute;
  private bar!: THREE.InstancedBufferAttribute;
  private col!: THREE.InstancedBufferAttribute;
  private capacity = 0;
  private n = 0;
  private readonly tint = new THREE.Color();

  constructor(capacity = 64) {
    const quad = new THREE.PlaneGeometry(1, 1);
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = quad.index;
    this.geo.setAttribute("position", quad.getAttribute("position"));
    this.geo.setAttribute("uv", quad.getAttribute("uv"));
    this.grow(capacity);
    const mat = new THREE.ShaderMaterial({
      uniforms: { uSize: { value: new THREE.Vector2(HP_BAR.width, HP_BAR.height) }, uBack: { value: new THREE.Color(HP_BAR.backing) } },
      vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthTest: false, depthWrite: false,
    });
    this.mesh = new THREE.Mesh(this.geo, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 20;                           // over the units, the rings and the scene's transparents
    this.mesh.name = "hp_bars";
  }

  begin(): void { this.n = 0; }

  /** One bar centred at (x, y, z); `color` is the team fill. Bars with alpha 0 are skipped. */
  push(x: number, y: number, z: number, s: BarState, color: number): void {
    if (s.alpha <= 0.001) return;
    if (this.n >= this.capacity) this.grow(this.capacity * 2);
    const i = this.n++;
    this.pos.setXYZ(i, x, y, z);
    this.bar.setXYZW(i, s.hp, s.chase, s.alpha, 0);
    this.tint.set(color);
    this.col.setXYZ(i, this.tint.r, this.tint.g, this.tint.b);
  }

  end(): void {
    this.geo.instanceCount = this.n;
    this.mesh.visible = this.n > 0;
    if (this.n === 0) return;
    dirty(this.pos, this.n); dirty(this.bar, this.n); dirty(this.col, this.n);
  }

  /** Frees the instanced geometry and the shader material this owns (the level calls it on dispose). */
  dispose(): void { this.geo.dispose(); (this.mesh.material as THREE.Material).dispose(); }

  private grow(capacity: number): void {
    const keep = this.n;
    const pos = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3).setUsage(THREE.DynamicDrawUsage);
    const bar = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(THREE.DynamicDrawUsage);
    const col = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3).setUsage(THREE.DynamicDrawUsage);
    if (this.capacity > 0) { pos.array.set(this.pos.array); bar.array.set(this.bar.array); col.array.set(this.col.array); this.geo.dispose(); }
    this.pos = pos; this.bar = bar; this.col = col;
    this.geo.setAttribute("aPos", pos);
    this.geo.setAttribute("aBar", bar);
    this.geo.setAttribute("aCol", col);
    this.capacity = capacity;
    this.n = keep;
  }
}
